import React from 'react';
import { ExternalLink } from 'lucide-react';
import { useContent } from '../../context/ContentContext';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';

const SponsorSection = () => {
  const { t } = useLanguage();
  const { sponsor } = useContent();

  return (
    <section className="relative py-32 bg-white overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#D4AF37]" />
            <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">
              {t('partnership', { de: 'Partnerschaft', en: 'Partnership' })}
            </span>
            <div className="h-px w-12 bg-[#D4AF37]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#003366]">
            {t('sponsor', translations.partners)}
          </h2>
        </div>

        {/* Sponsor Card */}
        <div className="max-w-4xl mx-auto">
          <div className="group relative p-12 rounded-3xl bg-[#F9F9F9] border border-black/5 hover:border-[#D4AF37]/30 transition-all duration-500">
            {/* Top-left corner frame with animated golden glow - extends to middle */}
            <div className="absolute top-0 left-0 w-1/2 h-1/2 overflow-hidden rounded-tl-3xl pointer-events-none">
              {/* Vertical black line */}
              <div className="absolute top-0 left-0 w-[2px] h-full bg-black">
                <div 
                  className="absolute left-0 w-full h-[60px]"
                  style={{
                    background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                    animation: 'cornerGlowVertical 4s ease-in-out infinite',
                    boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
                  }}
                />
              </div>
              {/* Horizontal black line */}
              <div className="absolute top-0 left-0 h-[2px] w-full bg-black">
                <div 
                  className="absolute top-0 h-full w-[60px]"
                  style={{
                    background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                    animation: 'cornerGlowHorizontal 4s ease-in-out infinite',
                    boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
                  }}
                />
              </div>
            </div>
            
            {/* Bottom-right corner frame with animated golden glow - extends to middle */}
            <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden rounded-br-3xl pointer-events-none">
              {/* Vertical black line */}
              <div className="absolute bottom-0 right-0 w-[2px] h-full bg-black">
                <div 
                  className="absolute right-0 w-full h-[60px]"
                  style={{
                    background: 'linear-gradient(0deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                    animation: 'cornerGlowVerticalReverse 4s ease-in-out infinite',
                    boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
                  }}
                />
              </div>
              {/* Horizontal black line */}
              <div className="absolute bottom-0 right-0 h-[2px] w-full bg-black">
                <div 
                  className="absolute bottom-0 h-full w-[60px]"
                  style={{
                    background: 'linear-gradient(270deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                    animation: 'cornerGlowHorizontalReverse 4s ease-in-out infinite',
                    boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-center gap-12">
              {/* Logo - Original colors */}
              <div className="flex-shrink-0">
                <div className="relative w-48 h-48 rounded-2xl bg-white p-6 flex items-center justify-center shadow-lg shadow-black/5">
                  <img src={sponsor.logo} alt={sponsor.name} className="w-full h-full object-contain" />
                </div>
              </div>

              <div className="flex-1 text-center lg:text-left">
                <h3 className="text-3xl font-bold text-[#003366] mb-4">{sponsor.name}</h3>
                <p className="text-black/60 text-lg leading-relaxed mb-6">
                  {t('sponsorDesc', translations.partners)}
                </p>
                <a href="https://www.albaberlin.de" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#003366] hover:bg-[#C9A227] rounded-md border border-[#B8960C] transition-all duration-300 font-medium">
                  <span>{t('visitWebsite', { de: 'Website besuchen', en: 'Visit Website' })}</span>
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Corner Glow Animations - Now in index.css */}
    </section>
  );
};

export default SponsorSection;
